/* eslint-disable camelcase */
// The middle section of the unified dialog when a client advertising report
// is the chosen type.
//
// Everything here changes what the recipient actually receives — which
// locations the document covers, which platforms it draws on, which months,
// and which files are attached — so none of it is hidden behind "More
// options". The one advanced control of this family, the custom date range,
// lives in ScheduleAdvancedFields.
import PropTypes from 'prop-types';
import { Alert, Autocomplete, MenuItem, Stack, TextField } from '@mui/material';

export const PLATFORM_CHOICES = [
    ['google_ads,meta', 'Google Ads and Meta'],
    ['google_ads,facebook', 'Google Ads and Facebook only'],
    ['google_ads', 'Google Ads'],
    ['meta', 'Meta (all publishers)'],
    ['facebook', 'Facebook only'],
];

export const FORMAT_CHOICES = [
    ['PDF', 'PDF'],
    ['EXCEL', 'Excel'],
    ['PDF,EXCEL', 'PDF and Excel'],
];

export default function ScheduleClientFields({
    form,
    update,
    onPlatformsChange,
    busy,
    locations,
    locationsLoading,
    locationsError,
    showPlatforms = true,
}) {
    return (
        <Stack spacing={2.5}>
            <TextField
                label="Report name"
                value={form.title}
                onChange={(event) => update('title', event.target.value)}
                disabled={busy}
                inputProps={{ maxLength: 200 }}
                required
                fullWidth
            />
            {/* Locations are chosen by name. The practice id is an internal
                key: it is what the payload carries, never what is shown. */}
            <Autocomplete
                multiple
                options={locations}
                value={locations.filter((practice) => form.practice_ids.includes(practice.id))}
                getOptionLabel={(practice) => practice.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(event, value) =>
                    update(
                        'practice_ids',
                        value.map((practice) => practice.id)
                    )
                }
                disabled={busy || locationsLoading || !!locationsError}
                loading={locationsLoading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Locations"
                        helperText="Only authorised locations are shown. Up to 25 locations."
                    />
                )}
            />
            {!locationsLoading && !locationsError && !locations.length && (
                <Alert severity="info">{showPlatforms ? 'No authorised locations are available for these platforms.' : 'No locations are available for this report.'}</Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {showPlatforms && (
                    <TextField
                        select
                        label="Platforms"
                        value={form.platforms.join(',')}
                        disabled={busy}
                        fullWidth
                        onChange={(event) => onPlatformsChange(event.target.value.split(','))}
                    >
                        {PLATFORM_CHOICES.map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                <TextField
                    select
                    label="Download formats"
                    value={form.formats.join(',')}
                    onChange={(event) => update('formats', event.target.value.split(','))}
                    disabled={busy}
                    fullWidth
                >
                    {FORMAT_CHOICES.map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
            <TextField
                select
                label="Reporting period"
                value={form.period === 'custom' ? 'custom' : form.period}
                disabled={busy || form.period === 'custom'}
                onChange={(event) => update('period', event.target.value)}
                helperText={
                    form.period === 'custom'
                        ? 'A custom range is set under More options. Clear it to schedule this report.'
                        : 'The months the report covers. The delivery date below is separate.'
                }
                fullWidth
            >
                <MenuItem value="previous_month">Previous complete calendar month</MenuItem>
                <MenuItem value="previous_three_months">Previous three complete calendar months</MenuItem>
                {form.period === 'custom' && (
                    <MenuItem value="custom">Custom dates (preview and download only)</MenuItem>
                )}
            </TextField>
        </Stack>
    );
}

ScheduleClientFields.propTypes = {
    showPlatforms: PropTypes.bool,
    form: PropTypes.object.isRequired,
    update: PropTypes.func.isRequired,
    onPlatformsChange: PropTypes.func.isRequired,
    busy: PropTypes.bool,
    locations: PropTypes.array.isRequired,
    locationsLoading: PropTypes.bool,
    locationsError: PropTypes.string,
};
